/**
 * src/services/weekly.api.ts 의 `team` 짝 — Weekly 관리자 탭의 지난주 인원별 현황.
 *
 * Weekly 처럼 만들어 두지 않고 부를 때마다 db.dailies 에서 모은다. 보는 주의 앞 주(지난주) 평일마다
 * 낸 Daily · 승인된 종일 휴가 · 공휴일로 그날 상태를 정하고, 사람마다 시간과 프로젝트별 투입을 더한다.
 * 남의 Daily 를 사람 단위로 모은 것이라 관리자만 본다 — 서버도 403 으로 막는다. 보는 관리자 자신은 뺀다
 * (Daily 제출현황과 같다).
 */
import { HttpResponse, http } from "msw";
import { shiftDate, today, weekStartOf } from "@/lib/dates";
import type { WeeklyTeam, WeeklyTeamDay, WeeklyTeamMember, WeeklyTeamProject } from "@/types";
import { db } from "../db";
import { fail, latency, requireUser } from "../http";

const round = (value: number) => Math.round(value * 100) / 100;
const sum = (values: number[]) => round(values.reduce((a, b) => a + b, 0));

function dayOf(userId: string, date: string, now: string): WeeklyTeamDay {
  const daily = db.dailies.find((d) => d.user_id === userId && d.work_date === date);
  const holiday = db.holidays.find((h) => h.holiday_date === date);
  // 종일 휴가만 그날을 비운다 — 반차 같은 부분 휴가는 그날도 Daily 를 낸다.
  const leave = db.vacations.find(
    (v) =>
      v.user_id === userId &&
      v.status === "approved" &&
      v.start_time === null &&
      v.start_date <= date &&
      v.end_date >= date
  );
  const hours = daily ? round(daily.regular_hours + daily.overtime_hours) : 0;
  const base = { work_date: date, hours, label: null, color: null };
  if (daily) return { ...base, state: "submitted" };
  if (holiday) return { ...base, state: "holiday", label: holiday.name };
  if (leave) return { ...base, state: "leave", label: leave.kind_name, color: leave.kind_color };
  if (date >= now) return { ...base, state: "upcoming" };
  return { ...base, state: "missing" };
}

function memberOf(user: (typeof db.users)[number], from: string, now: string): WeeklyTeamMember {
  const dates = Array.from({ length: 5 }, (_, i) => shiftDate(from, i));
  const days = dates.map((date) => dayOf(user.id, date, now));
  const dailies = db.dailies.filter(
    (d) => d.user_id === user.id && d.work_date >= from && d.work_date <= shiftDate(from, 6)
  );
  const rows = dailies.flatMap((d) => d.rows);

  const byProject = new Map<string, WeeklyTeamProject>();
  for (const row of rows) {
    if (row.actual_hours <= 0) continue;
    const key = row.project_id ?? "other";
    const project = row.project_id ? db.projects.find((p) => p.id === row.project_id) : undefined;
    const line = byProject.get(key) ?? {
      project_id: row.project_id,
      code: project?.code ?? null,
      name: project?.name ?? "프로젝트 외 업무",
      color: project?.color ?? "",
      hours: 0,
    };
    line.hours = round(line.hours + row.actual_hours);
    byProject.set(key, line);
  }

  const regular = sum(dailies.map((d) => d.regular_hours));
  const overtime = sum(dailies.map((d) => d.overtime_hours));
  const planHours = sum(rows.map((r) => r.plan_hours));
  const workdays = days.filter((d) => d.state !== "leave" && d.state !== "holiday").length;
  return {
    user_id: user.id,
    user_name: user.name,
    days,
    submitted: days.filter((d) => d.state === "submitted").length,
    due: days.filter((d) => d.state === "submitted" || d.state === "missing").length,
    regular_hours: regular,
    overtime_hours: overtime,
    total_hours: round(regular + overtime),
    standard_hours: round(
      (user.standard_daily_hours ?? db.siteConfig.standard_daily_hours) * workdays
    ),
    plan_hours: planHours,
    // 계획 없이 더한 줄은 견줄 계획이 없다 — 계획이 있는 줄의 실제 시간만 계획과 견준다.
    plan_actual_hours: sum(rows.filter((r) => r.plan_hours > 0).map((r) => r.actual_hours)),
    plan_achievement: planHours
      ? round(sum(rows.filter((r) => r.plan_hours > 0).map((r) => r.actual_hours)) / planHours)
      : null,
    // 프로젝트 외 업무는 시간과 상관없이 맨 뒤.
    projects: [...byProject.values()].sort(
      (a, b) => Number(a.project_id === null) - Number(b.project_id === null) || b.hours - a.hours
    ),
  };
}

export const weeklyTeamHandlers = [
  http.get("/api/v1/weekly/team/", async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    if (user.role !== "admin") return fail(403, "관리자만 볼 수 있습니다.");
    const weekOf = new URL(request.url).searchParams.get("week_of") || today();
    const from = shiftDate(weekStartOf(weekOf), -7);
    const now = today();
    const members = db.users
      .filter(
        (u) =>
          u.status === "active" &&
          u.id !== user.id &&
          (u.hired_on === null || u.hired_on <= shiftDate(from, 6))
      )
      .sort((a, b) => a.name.localeCompare(b.name, "ko"))
      .map((u) => memberOf(u, from, now));
    const body: WeeklyTeam = { week_start: from, members };
    return HttpResponse.json(body);
  }),
];
